import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBookingsTimeslotsConfigurationV1TimeSlotsConfiguration from './bookings-timeslots-configuration-v1-time-slots-configuration-time-slots-configuration.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** Settings that control how available booking times are displayed to customers. */
export interface TimeSlotsConfiguration {
  /**
   * Time slots configuration ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the time slots configuration is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the time slots configuration.
   *
   * Ignored when creating a time slots configuration.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the time slots configuration was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the time slots configuration was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Time zone display settings. */
  displayTimeZone?: DisplayTimeZone;
  /** Staff selection settings that control how staff members are assigned to bookings. */
  staffSelection?: StaffSelection;
  /** Time interval settings that control how available time slots are divided. */
  splitInterval?: SplitInterval;
  /**
   * App that owns this configuration. Set by the Bookings platform when the time slots configuration is created via [Provision Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/bookings-platform-provisioner/provision-bookings-platform).
   * @format GUID
   */
  appId?: string;
  /**
   * Custom field data for the time slots configuration object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

export interface DisplayTimeZone {
  /**
   * Whether the customer can change the time zone when booking or viewing an existing booking.
   *
   * Default: `true`
   */
  customerCanChange?: boolean | null;
  /**
   * Time zone used to display available times, either based on the business's or customer's location.
   *
   * Default: `BUSINESS_TIME_ZONE`
   */
  basedOn?: BasedOnWithLiterals;
  /**
   * The business's time zone, in IANA time zone format (for example, `America/New_York`).
   * Derived from the site's business properties.
   * @maxLength 255
   * @readonly
   */
  timeZone?: string | null;
}

export enum BasedOn {
  /** Display time zone is based on the business's location. */
  BUSINESS_TIME_ZONE = 'BUSINESS_TIME_ZONE',
  /** Display time zone is based on the customer's location. */
  CUSTOMER_TIME_ZONE = 'CUSTOMER_TIME_ZONE',
}

/** @enumType */
export type BasedOnWithLiterals =
  | BasedOn
  | 'BUSINESS_TIME_ZONE'
  | 'CUSTOMER_TIME_ZONE';

export interface StaffSelection {
  /** Staff selection strategy. */
  strategy?: StrategyWithLiterals;
}

export enum Strategy {
  /** Customer must explicitly select a staff member. Booking fails if no staff is selected. */
  CUSTOMER_MUST_CHOOSE = 'CUSTOMER_MUST_CHOOSE',
  /** Customer can select a specific staff member or choose "any available". */
  CUSTOMER_MAY_CHOOSE = 'CUSTOMER_MAY_CHOOSE',
  /** Staff selection is hidden from the customer. The system auto-assigns a staff member. */
  CUSTOMER_CANNOT_CHOOSE = 'CUSTOMER_CANNOT_CHOOSE',
}

/** @enumType */
export type StrategyWithLiterals =
  | Strategy
  | 'CUSTOMER_MUST_CHOOSE'
  | 'CUSTOMER_MAY_CHOOSE'
  | 'CUSTOMER_CANNOT_CHOOSE';

/** Interval between available slot start times. For example, 15 minutes to display available slots at 3:00, 3:15, and 3:30 PM. */
export interface SplitInterval {
  /**
   * Whether the slot duration is used as the interval value.
   * - `true`: The interval is identical to the service's slot duration.
   * - `false`: The interval is a fixed number of minutes specified in `valueInMinutes`.
   *
   * Default: `false`
   */
  sameAsDuration?: boolean | null;
  /**
   * Number of minutes between available slot start times.
   * Ignored when `sameAsDuration: true`.
   *
   * Default: `30`
   */
  valueInMinutes?: number | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateTimeSlotsConfigurationRequest {
  /** Time slots configuration to create. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface CreateTimeSlotsConfigurationResponse {
  /** Created time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface GetTimeSlotsConfigurationRequest {
  /**
   * Time slots configuration ID.
   * @format GUID
   */
  timeSlotsConfigurationId: string;
}

export interface GetTimeSlotsConfigurationResponse {
  /** Retrieved time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface GetTimeSlotsConfigurationByAppIdRequest {
  /**
   * App ID.
   * @format GUID
   */
  appId: string;
}

export interface GetTimeSlotsConfigurationByAppIdResponse {
  /** Retrieved time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface UpdateTimeSlotsConfigurationRequest {
  /** Time slots configuration to update. */
  timeSlotsConfiguration: TimeSlotsConfiguration;
}

export interface UpdateTimeSlotsConfigurationResponse {
  /** Updated time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface QueryTimeSlotsConfigurationsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryTimeSlotsConfigurationsResponse {
  /** List of time slots configurations. */
  timeSlotsConfigurations?: TimeSlotsConfiguration[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Retrieves a time slots configuration by ID.
 * @param timeSlotsConfigurationId - Time slots configuration ID.
 * @internal
 * @documentationMaturity preview
 * @requiredField timeSlotsConfigurationId
 * @permissionId TIMESLOTS_CONFIGURATION.READ
 * @applicableIdentity APP
 * @returns Retrieved time slots configuration.
 * @fqn wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.GetTimeSlotsConfiguration
 */
export async function getTimeSlotsConfiguration(
  timeSlotsConfigurationId: string
): Promise<
  NonNullablePaths<
    TimeSlotsConfiguration,
    `displayTimeZone.basedOn` | `staffSelection.strategy` | `appId`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    timeSlotsConfigurationId: timeSlotsConfigurationId,
  });

  const reqOpts =
    ambassadorWixBookingsTimeslotsConfigurationV1TimeSlotsConfiguration.getTimeSlotsConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.timeSlotsConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { timeSlotsConfigurationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['timeSlotsConfigurationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a time slots configuration by app ID.
 * @param appId - App ID.
 * @internal
 * @documentationMaturity preview
 * @requiredField appId
 * @permissionId TIMESLOTS_CONFIGURATION.READ
 * @applicableIdentity APP
 * @fqn wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.GetTimeSlotsConfigurationByAppId
 */
export async function getTimeSlotsConfigurationByAppId(
  appId: string
): Promise<
  NonNullablePaths<
    GetTimeSlotsConfigurationByAppIdResponse,
    | `timeSlotsConfiguration.displayTimeZone.basedOn`
    | `timeSlotsConfiguration.staffSelection.strategy`
    | `timeSlotsConfiguration.appId`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ appId: appId });

  const reqOpts =
    ambassadorWixBookingsTimeslotsConfigurationV1TimeSlotsConfiguration.getTimeSlotsConfigurationByAppId(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { appId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['appId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a time slots configuration.
 *
 * Each time the time slots configuration is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the time slots configuration.
 * This ensures you're working with the latest time slots configuration and prevents unintended overwrites.
 * @param _id - Time slots configuration ID.
 * @internal
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField timeSlotsConfiguration
 * @requiredField timeSlotsConfiguration.revision
 * @permissionId TIMESLOTS_CONFIGURATION.UPDATE
 * @returns Updated time slots configuration.
 * @fqn wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.UpdateTimeSlotsConfiguration
 */
export async function updateTimeSlotsConfiguration(
  _id: string,
  timeSlotsConfiguration: NonNullablePaths<
    UpdateTimeSlotsConfiguration,
    `revision`,
    2
  >
): Promise<
  NonNullablePaths<
    TimeSlotsConfiguration,
    `displayTimeZone.basedOn` | `staffSelection.strategy` | `appId`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    timeSlotsConfiguration: { ...timeSlotsConfiguration, id: _id },
  });

  const reqOpts =
    ambassadorWixBookingsTimeslotsConfigurationV1TimeSlotsConfiguration.updateTimeSlotsConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.timeSlotsConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { timeSlotsConfiguration: '$[1]' },
        explicitPathsToArguments: { 'timeSlotsConfiguration.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'timeSlotsConfiguration']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateTimeSlotsConfiguration {
  /**
   * Time slots configuration ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the time slots configuration is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the time slots configuration.
   *
   * Ignored when creating a time slots configuration.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the time slots configuration was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the time slots configuration was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Time zone display settings. */
  displayTimeZone?: DisplayTimeZone;
  /** Staff selection settings that control how staff members are assigned to bookings. */
  staffSelection?: StaffSelection;
  /** Time interval settings that control how available time slots are divided. */
  splitInterval?: SplitInterval;
  /**
   * App that owns this configuration. Set by the Bookings platform when the time slots configuration is created via [Provision Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/bookings-platform-provisioner/provision-bookings-platform).
   * @format GUID
   */
  appId?: string;
  /**
   * Custom field data for the time slots configuration object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

/**
 * Retrieves a list of time slots configurations, given the provided paging, filtering, and sorting.
 * @internal
 * @documentationMaturity preview
 * @permissionId bookings:timeslots_configuration:v1:time_slots_configuration:query_time_slots_configurations
 * @applicableIdentity APP
 * @fqn wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.QueryTimeSlotsConfigurations
 */
export function queryTimeSlotsConfigurations(): TimeSlotsConfigurationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    TimeSlotsConfiguration,
    'CURSOR',
    QueryTimeSlotsConfigurationsRequest,
    QueryTimeSlotsConfigurationsResponse
  >({
    func: async (payload: QueryTimeSlotsConfigurationsRequest) => {
      const reqOpts =
        ambassadorWixBookingsTimeslotsConfigurationV1TimeSlotsConfiguration.queryTimeSlotsConfigurations(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (
      query: QueryTimeSlotsConfigurationsRequest['query']
    ) => {
      const args = [query, {}] as [
        QueryTimeSlotsConfigurationsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryTimeSlotsConfigurationsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.timeSlotsConfigurations,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface TimeSlotsConfigurationsQueryResult extends QueryCursorResult {
  items: TimeSlotsConfiguration[];
  query: TimeSlotsConfigurationsQueryBuilder;
  next: () => Promise<TimeSlotsConfigurationsQueryResult>;
  prev: () => Promise<TimeSlotsConfigurationsQueryResult>;
}

export interface TimeSlotsConfigurationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'displayTimeZone.basedOn'
      | 'staffSelection.strategy'
      | 'splitInterval.sameAsDuration'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'displayTimeZone.basedOn'
      | 'staffSelection.strategy'
      | 'splitInterval.sameAsDuration'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'appId',
    value: string
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'displayTimeZone.basedOn'
      | 'staffSelection.strategy'
      | 'splitInterval.sameAsDuration'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any[]
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'displayTimeZone.basedOn'
      | 'staffSelection.strategy'
      | 'splitInterval.sameAsDuration'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: any
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'displayTimeZone.basedOn'
      | 'staffSelection.strategy'
      | 'splitInterval.sameAsDuration'
      | 'splitInterval.valueInMinutes'
      | 'appId',
    value: boolean
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<'_id' | '_createdDate' | '_updatedDate'>
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<'_id' | '_createdDate' | '_updatedDate'>
  ) => TimeSlotsConfigurationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => TimeSlotsConfigurationsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => TimeSlotsConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<TimeSlotsConfigurationsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.QueryTimeSlotsConfigurations
 * @requiredField query
 */
export async function typedQueryTimeSlotsConfigurations(
  query: TimeSlotsConfigurationQuery
): Promise<
  NonNullablePaths<
    QueryTimeSlotsConfigurationsResponse,
    | `timeSlotsConfigurations`
    | `timeSlotsConfigurations.${number}.displayTimeZone.basedOn`
    | `timeSlotsConfigurations.${number}.staffSelection.strategy`
    | `timeSlotsConfigurations.${number}.appId`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixBookingsTimeslotsConfigurationV1TimeSlotsConfiguration.queryTimeSlotsConfigurations(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface TimeSlotsConfigurationQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id', '_updatedDate'];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: ['appId'];
      operators: '*';
      sort: 'NONE';
    },
    {
      fields: [
        'displayTimeZone.basedOn',
        'splitInterval.sameAsDuration',
        'splitInterval.valueInMinutes',
        'staffSelection.strategy'
      ];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  TimeSlotsConfiguration,
  TimeSlotsConfigurationQuerySpec
>;
export type TimeSlotsConfigurationQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      TimeSlotsConfiguration,
      TimeSlotsConfigurationQuerySpec,
      TimeSlotsConfigurationQuery
    >(),
  },
};
